import { useState, useRef } from 'react';
import { Upload, X, AlertCircle, CheckCircle, Loader2, FileMusic, FileImage, FileText } from 'lucide-react';
import type { ScanResult } from '../../types';

interface SongFiles {
  baseName: string;
  mp3?: File;
  jpg?: File;
  md?: File;
}

interface UploadResult {
  saved: string[];
  errors: Array<{ filename: string; error: string }>;
  scanResult: ScanResult | null;
}

export function SongUpload() {
  const [songGroups, setSongGroups] = useState<SongFiles[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Group files by base name
    const groups = new Map<string, SongFiles>();
    const invalidFiles: string[] = [];

    files.forEach((file) => {
      const fileName = file.name;
      const extension = fileName.split('.').pop()?.toLowerCase();
      const baseName = fileName.substring(0, fileName.lastIndexOf('.'));

      // Validate extension
      const validExtensions = ['mp3', 'jpg', 'jpeg', 'png', 'md'];
      if (!extension || !validExtensions.includes(extension)) {
        invalidFiles.push(fileName);
        return;
      }

      if (!groups.has(baseName)) {
        groups.set(baseName, { baseName });
      }

      const group = groups.get(baseName)!;

      if (extension === 'mp3') {
        group.mp3 = file;
      } else if (extension === 'jpg' || extension === 'jpeg' || extension === 'png') {
        group.jpg = file;
      } else if (extension === 'md') {
        group.md = file;
      }
    });

    setSongGroups(Array.from(groups.values()));
    setUploadResult(null);

    // Show warning for invalid files
    if (invalidFiles.length > 0) {
      setUploadResult({
        saved: [],
        errors: invalidFiles.map(name => ({
          filename: name,
          error: 'Invalid file type. Only MP3, JPG, PNG, and MD files are supported.'
        })),
        scanResult: null,
      });
    }
  };

  const removeGroup = (baseName: string) => {
    setSongGroups(songGroups.filter((g) => g.baseName !== baseName));
  };

  const handleUpload = async () => {
    if (songGroups.length === 0) return;

    // Validate all groups are complete
    const incompleteGroups = songGroups.filter(g => !isGroupComplete(g));
    if (incompleteGroups.length > 0) {
      const errors = incompleteGroups.map(group => {
        const missing: string[] = [];
        if (!group.mp3) missing.push('MP3');
        if (!group.jpg) missing.push('image (JPG/PNG)');
        if (!group.md) missing.push('MD');

        return {
          filename: group.baseName,
          error: `Incomplete song group - missing: ${missing.join(', ')}. Each song requires an MP3, image (JPG/PNG), and MD file.`
        };
      });

      setUploadResult({
        saved: [],
        errors,
        scanResult: null,
      });
      return;
    }

    setUploading(true);
    setUploadResult(null);

    try {
      const formData = new FormData();

      // Add all files to form data
      songGroups.forEach((group) => {
        if (group.mp3) formData.append('files', group.mp3);
        if (group.jpg) formData.append('files', group.jpg);
        if (group.md) formData.append('files', group.md);
      });

      const response = await fetch('/api/songs/upload', {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Upload failed');
      }

      const result = await response.json();
      setUploadResult(result);

      // Clear the form if successful
      if (result.saved.length > 0) {
        setSongGroups([]);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    } catch (error) {
      setUploadResult({
        saved: [],
        errors: [{ filename: 'upload', error: (error as Error).message }],
        scanResult: null,
      });
    } finally {
      setUploading(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const isGroupComplete = (group: SongFiles): boolean => {
    return !!(group.mp3 && group.jpg && group.md);
  };

  const totalFiles = songGroups.reduce((acc, g) => {
    let count = 0;
    if (g.mp3) count++;
    if (g.jpg) count++;
    if (g.md) count++;
    return acc + count;
  }, 0);

  return (
    <div className="card">
      <h2 className="text-2xl font-bold mb-6">Upload Songs</h2>

      {/* File Input */}
      <div className="mb-6">
        <label className="block mb-4">
          <div className="flex items-center justify-center w-full h-32 border-2 border-dashed border-monster-border rounded-lg hover:border-monster-blue transition-colors cursor-pointer">
            <div className="text-center">
              <Upload className="mx-auto mb-2 text-gray-400" size={32} />
              <p className="text-sm text-gray-400">
                Click to select files or drag and drop
              </p>
              <p className="text-xs text-gray-500 mt-1">
                MP3 (audio), JPG/PNG (artwork), MD (metadata)
              </p>
            </div>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".mp3,.jpg,.jpeg,.png,.md"
            onChange={handleFileSelect}
            className="hidden"
          />
        </label>

        <p className="text-xs text-gray-500">
          Files with matching names (e.g., song-name.mp3, song-name.png, song-name.md) will be grouped together.
          Uploading files with existing names will overwrite the old files.
        </p>
      </div>

      {/* Song Groups */}
      {songGroups.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold">
              {songGroups.length} song{songGroups.length !== 1 ? 's' : ''} ({totalFiles} files)
            </h3>
            <button
              onClick={() => setSongGroups([])}
              className="text-sm text-gray-400 hover:text-white"
            >
              Clear All
            </button>
          </div>

          <div className="space-y-3 max-h-96 overflow-y-auto">
            {songGroups.map((group) => (
              <div
                key={group.baseName}
                className={`bg-monster-dark border rounded-lg p-4 ${
                  isGroupComplete(group)
                    ? 'border-green-500/30'
                    : 'border-yellow-500/30'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h4 className="font-medium text-sm mb-1">{group.baseName}</h4>
                    {!isGroupComplete(group) && (
                      <p className="text-xs text-yellow-500 flex items-center gap-1">
                        <AlertCircle size={12} />
                        Incomplete: Missing {[
                          !group.mp3 && 'MP3',
                          !group.jpg && 'Image (JPG/PNG)',
                          !group.md && 'MD',
                        ].filter(Boolean).join(', ')}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => removeGroup(group.baseName)}
                    className="text-gray-400 hover:text-white"
                  >
                    <X size={16} />
                  </button>
                </div>

                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div className={`flex items-center gap-1 ${group.mp3 ? 'text-green-400' : 'text-gray-600'}`}>
                    <FileMusic size={14} />
                    <span>{group.mp3 ? formatFileSize(group.mp3.size) : 'No MP3'}</span>
                  </div>
                  <div className={`flex items-center gap-1 ${group.jpg ? 'text-green-400' : 'text-gray-600'}`}>
                    <FileImage size={14} />
                    <span>{group.jpg ? formatFileSize(group.jpg.size) : 'No Image'}</span>
                  </div>
                  <div className={`flex items-center gap-1 ${group.md ? 'text-green-400' : 'text-gray-600'}`}>
                    <FileText size={14} />
                    <span>{group.md ? formatFileSize(group.md.size) : 'No MD'}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Upload Button */}
          <button
            onClick={handleUpload}
            disabled={uploading || songGroups.length === 0}
            className="w-full btn-primary mt-4"
          >
            {uploading ? (
              <>
                <Loader2 className="inline animate-spin mr-2" size={20} />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="inline mr-2" size={20} />
                Upload {songGroups.length} Song{songGroups.length !== 1 ? 's' : ''}
              </>
            )}
          </button>
        </div>
      )}

      {/* Upload Result */}
      {uploadResult && (
        <div className="space-y-3">
          {uploadResult.saved.length > 0 && (
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={20} />
                <div className="flex-1">
                  <p className="font-semibold text-green-500 mb-2">
                    Successfully uploaded {uploadResult.saved.length} file{uploadResult.saved.length !== 1 ? 's' : ''}
                  </p>
                  <ul className="text-sm text-gray-300 space-y-1">
                    {uploadResult.saved.map((filename) => (
                      <li key={filename}>✓ {filename}</li>
                    ))}
                  </ul>
                  {uploadResult.scanResult && uploadResult.scanResult.total > 0 && (
                    <div className="text-sm text-gray-400 mt-3 pt-3 border-t border-green-500/20">
                      <p className="font-semibold mb-1">Scan Result:</p>
                      <p>{uploadResult.scanResult.total} song{uploadResult.scanResult.total !== 1 ? 's' : ''} processed in database</p>
                      {uploadResult.scanResult.added.length > 0 && (
                        <p>✓ Added: {uploadResult.scanResult.added.join(', ')}</p>
                      )}
                      {uploadResult.scanResult.updated.length > 0 && (
                        <p>↻ Updated: {uploadResult.scanResult.updated.join(', ')}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {uploadResult.errors.length > 0 && (
            <div className="bg-monster-crimson/10 border border-monster-crimson/30 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="text-monster-crimson flex-shrink-0 mt-0.5" size={20} />
                <div className="flex-1">
                  <p className="font-semibold text-monster-crimson mb-2">
                    {uploadResult.errors.length} error{uploadResult.errors.length !== 1 ? 's' : ''}
                  </p>
                  <ul className="text-sm text-gray-300 space-y-1">
                    {uploadResult.errors.map((error, idx) => (
                      <li key={idx}>✗ {error.filename}: {error.error}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
